import { useMemo } from 'react';

import date from 'date-and-time';

import { DetailRow } from './DetailRow';
import { Card } from '../../components/base/Card';
import { SectionLoading } from '../shared/SectionLoading';
import useAppliances from '../../data/appliance/useAppliances';
import { useApplianceLookups } from '../../hooks/useApplianceLookups';
import Skeleton from '../shared/Skeleton';

interface ApplianceDetailsProps {
  applianceId: string;
}

export function FaultApplianceDetails({ applianceId }: ApplianceDetailsProps) {
  const { appliances } = useAppliances();
  const { isLoading, typeLookup, manufacturerLookup, modelLookup } =
    useApplianceLookups();

  const appliance = useMemo(() => {
    return appliances.data?.find((a) => a.id === applianceId);
  }, [appliances, applianceId]);

  const type = useMemo(
    () => (appliance ? typeLookup(appliance.hardwareTypeId) : null),
    [appliance, typeLookup]
  );

  const manufacturer = useMemo(
    () =>
      appliance ? manufacturerLookup(appliance.hardwareManufacturerId) : null,
    [appliance, manufacturerLookup]
  );

  const model = useMemo(
    () => (appliance ? modelLookup(appliance.hardwareModelId) : null),
    [appliance, modelLookup]
  );

  return (
    <Card className="lg:col-span-2 space-y-4">
      <span className="text-lg font-medium">Appliance Details</span>
      {isLoading || appliances.isLoading ? (
        <SectionLoading size="sm" />
      ) : (
        <dl className="divide-y divide-gray-100 dark:divide-white/10">
          <DetailRow
            title="Type"
            value={type?.name ? type.name : 'Unavailable'}
          />
          <DetailRow
            title="Manufacturer"
            value={manufacturer?.name ? manufacturer.name : 'Unavailable'}
          />
          <DetailRow
            title="Model"
            value={model?.name ? model.name : 'Unavailable'}
          />
          <DetailRow
            title="Serial Number"
            value={appliance?.reference ? appliance.reference : 'Unavailable'}
          />
          <DetailRow
            title="Service Due"
            value={
              appliance?.serviceDue
                ? date.format(new Date(appliance.serviceDue), 'DD/MM/YYYY')
                : 'Unavailable'
            }
          />
        </dl>
      )}
    </Card>
  );
}
